import type { AskUserQuestionAnswer } from "./types";

/**
 * 查看态答案展示：多个 selectedOptions（或 freeInput 与选项并存时）之间的分隔符。
 * 产品口径为中文顿号「、」，勿改为逗号或斜杠。
 */
export const ASK_USER_QUESTION_ANSWER_SEPARATOR = "、";

/**
 * 运行时校验单条答案是否符合 AskUserQuestionAnswer 契约。
 * 用于 JSON.parse 后的数组过滤，避免脏数据导致渲染崩溃。
 */
export const isAskUserQuestionAnswer = (
  value: unknown,
): value is AskUserQuestionAnswer => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as AskUserQuestionAnswer;

  return (
    typeof candidate.questionIndex === "number" &&
    typeof candidate.question === "string" &&
    Array.isArray(candidate.selectedOptions) &&
    candidate.selectedOptions.every((option) => typeof option === "string") &&
    (candidate.freeInput === undefined ||
      typeof candidate.freeInput === "string")
  );
};

/**
 * 将 interactiveToolCalls[0].result 解析为答案数组。
 *
 * 背景：
 * - 提交时 content 为 JSON.stringify(AskUserQuestionAnswer[])。
 * - chatsDetail 回显时 result 常为同结构的 JSON 字符串（见 frontend-flow.md）。
 *
 * 支持：
 * - `string` → JSON.parse 后取数组
 * - 已是 `AskUserQuestionAnswer[]` 的数组（少数 API 直出）
 *
 * 不支持 / 返回 []：
 * - null、空串、非法 JSON、非数组、元素未通过 isAskUserQuestionAnswer
 */
export const parseAskUserQuestionResult = (
  result: unknown,
): AskUserQuestionAnswer[] => {
  if (result == null || result === "") {
    return [];
  }

  let parsed: unknown = result;

  if (typeof result === "string") {
    try {
      parsed = JSON.parse(result);
    } catch {
      return [];
    }
  }

  if (!Array.isArray(parsed)) {
    return [];
  }

  return parsed.filter(isAskUserQuestionAnswer);
};

/**
 * 将单题答案格式化为查看态正文（AskUserQuestionResultCard 内展示）。
 *
 * 规则：
 * 1. selectedOptions 去空后多项用 ASK_USER_QUESTION_ANSWER_SEPARATOR（、）拼接。
 * 2. freeInput 非空时：若已有选项文案，则 `选项文案、freeInput`；否则仅 freeInput。
 * 3. 全无内容返回空串，调用方应不渲染该卡。
 */
export const formatAskUserQuestionAnswerDisplay = (
  answer: AskUserQuestionAnswer,
): string => {
  const selectedText = answer.selectedOptions
    .map((option) => option.trim())
    .filter(Boolean)
    .join(ASK_USER_QUESTION_ANSWER_SEPARATOR);

  const freeInput = (answer.freeInput ?? "").trim();

  if (selectedText && freeInput) {
    return `${selectedText}${ASK_USER_QUESTION_ANSWER_SEPARATOR}${freeInput}`;
  }

  if (selectedText) {
    return selectedText;
  }

  return freeInput;
};

/**
 * 是否应进入 ask_user_question 查看态（Message 层用于压制交互态 args）。
 * 仅当 result 能解析出至少一条合法答案时为 true。
 */
export const hasAskUserQuestionResult = (result: unknown): boolean => {
  if (result == null || result === "") {
    return false;
  }

  return parseAskUserQuestionResult(result).length > 0;
};
